import { useEffect, useState } from 'react';
import { BoxV2 as Box, Flex, Button } from 'portal-commons';
import { useAuthContext } from '@/contexts';
import Modal from '@/components/ModalV2';
import Checkbox from '@/components/Checkbox';

const WELCOME_MODAL_STORAGE_KEY = 'suppress_welcome_popup';
const WELCOME_MODAL_SUPPRESSION_DAYS = 30;
const SUPPRESSION_MS = WELCOME_MODAL_SUPPRESSION_DAYS * 24 * 60 * 60 * 1000;

function GeneralAlertPopup() {
  const { user } = useAuthContext();
  const [isOpen, setIsOpen] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  useEffect(() => {
    // Check login and suppression state
    const oktaUserId = user?.id;
    if (!oktaUserId) {
      setIsOpen(false);
      return;
    }

    const generalAlert = localStorage.getItem('generalAlert');

    // Top priority: if the generalAlert flag is not there, don't show it.
    if (!generalAlert) {
      setIsOpen(false);
      return;
    }

    // We have the flag, consume it immediately so it won't show again on re-render or refresh
    localStorage.removeItem('generalAlert');

    const rawData = localStorage.getItem(WELCOME_MODAL_STORAGE_KEY);
    if (!rawData) {
      setIsOpen(true);
      return;
    }

    try {
      const preferences = JSON.parse(rawData);
      const userUntil = preferences?.[oktaUserId];

      // Check if the suppression period is still active for the current user
      if (userUntil && Date.now() < userUntil) {
        setIsOpen(false);
        return;
      }

      // Suppression has expired or does not exist -> Show modal
      setIsOpen(true);
    } catch (error) {
      console.error(
        'Failed to parse welcome modal preference from localStorage:',
        error
      );
      setIsOpen(true);
    }
  }, [user]);

  const handleClose = () => {
    const oktaUserId = user?.id;
    if (oktaUserId) {
      const rawData = localStorage.getItem(WELCOME_MODAL_STORAGE_KEY);
      let preferences = {};
      if (rawData) {
        try {
          preferences = JSON.parse(rawData) || {};
        } catch (e) {
          preferences = {};
        }
      }

      if (doNotShowAgain) {
        preferences[oktaUserId] = Date.now() + SUPPRESSION_MS;
        localStorage.setItem(
          WELCOME_MODAL_STORAGE_KEY,
          JSON.stringify(preferences)
        );
      } else {
        delete preferences[oktaUserId];
        if (Object.keys(preferences).length === 0) {
          localStorage.removeItem(WELCOME_MODAL_STORAGE_KEY);
        } else {
          localStorage.setItem(
            WELCOME_MODAL_STORAGE_KEY,
            JSON.stringify(preferences)
          );
        }
      }
    }
    setIsOpen(false);
  };

  return (
    <Modal
      open={isOpen}
      ariaLabelledby="general-alert-popup-title"
      data-testid="generalAlertPopup"
      padding="32px"
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          color: 't.grey500',
          lineHeight: 'normal',
          fontSize: '16px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box
            as="h2"
            id="general-alert-popup-title"
            sx={{
              fontWeight: 'bold',
              color: 't.grey500',
              fontSize: '20px',
              textAlign: 'center',
              margin: 0,
            }}
          >
            <Box as="p" sx={{ margin: 0 }}>
              Welcome to
            </Box>
            <Box as="p" sx={{ margin: 0, textTransform: 'uppercase' }}>
              The Campaign Registry
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            width: '100%',
          }}
        >
          <Box as="p" sx={{ margin: '0 0 16px 0' }}>
            <span style={{ fontWeight: 'bold' }}>Have suggestions?</span> We're
            constantly trying to improve our software to meet your needs. If you
            have any feedback, send an email to{' '}
            <Box
              as="a"
              href="mailto:suggestions@campaignregistry.com"
              sx={{
                color: 't.blue100',
                textDecoration: 'underline',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              suggestions@campaignregistry.com
            </Box>
            .
          </Box>
          <p>
            <span style={{ fontWeight: 'bold' }}>
              For technical help or troubleshooting,
            </span>
            &nbsp;contact us at&nbsp;
            <br />
            <Box
              as="a"
              href="mailto:support@campaignregistry.com"
              sx={{
                color: 't.blue100',
                textDecoration: 'underline',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              support@campaignregistry.com
            </Box>
            .
          </p>
          <br />
          The Campaign Registry Team
        </Box>

        <Flex
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Checkbox
            checked={doNotShowAgain}
            onChange={(checked) => setDoNotShowAgain(checked)}
            data-testid="suppressWelcomeCheckbox"
            label="Do not show again for 30 days"
          />
        </Flex>

        <Button
          type="button"
          onClick={handleClose}
          data-testid="okButton"
          size="medium"
          variant="standard"
          shape="rounded"
        >
          OK
        </Button>
      </Box>
    </Modal>
  );
}

export default GeneralAlertPopup;
